import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import Polygon from '@arcgis/core/geometry/Polygon';
import Extent from '@arcgis/core/geometry/Extent';
import type {
  DistanceResult,
  NearestPropertyRow,
} from './types';
import { postProcessRows, groupByManagementType, applyTableFilter as filterRows } from './postProcess';
import * as geodeticLengthOperator from '@arcgis/core/geometry/operators/geodeticLengthOperator.js';
import * as geodeticDensifyOperator from '@arcgis/core/geometry/operators/geodeticDensifyOperator.js';

import { getDistanceWorker } from './workerWrapper';
import Geometry from '@arcgis/core/geometry/Geometry';
import Polyline from '@arcgis/core/geometry/Polyline';

// ─── Options ─────────────────────────────────────────────────────────────────

/**
 * Already documented with JSDoc above the interface declaration.
 */
export interface UseNearestByGeometryOptions {
  /** JSON-serialized source geometry (Polygon, Extent, etc.) to measure distances from. */
  sourceGeometry: Record<string, unknown> | null | undefined;
  /** Target FeatureLayer to query for candidate features. */
  targetLayer: FeatureLayer | undefined;
  /** Optional WHERE clause to filter target layer features before distance computation. */
  where?: string;
  /** Hard cap on total results. Default: 5. */
  limit?: number;
  /** If true the hook is disabled and won't compute. */
  skip?: boolean;
}

/**
 * Already documented with JSDoc above the interface declaration.
 */
export interface UseNearestByGeometryResult {
  /** Nearest property rows sorted by distance ascending, enriched with lineGraphic + geodesic miles. */
  rows: NearestPropertyRow[];
  /** Rows grouped by managementType key. */
  rowsByType: Record<string, NearestPropertyRow[]>;
  /** Total unfiltered result count (before table filter). */
  total: number;
  /** Whether the worker is computing. */
  isLoading: boolean;
  /** Error message if computation failed. */
  error?: string;
  /** Apply a client-side text filter (post-worker, main thread). */
  applyTableFilter: (value: string) => void;
  /** Clear the client-side text filter. */
  clearTableFilter: () => void;
  /** Current table filter value. */
  tableFilter: string;
}

// ─── Hook ────────────────────────────────────────────────────────────────────

/**
 * Simplified hook: computes nearest features on `targetLayer` to a raw source geometry.
 *
 * Unlike `useNearestProperties`, this hook does NOT look up the source by ID —
 * the caller provides the source geometry directly (JSON-serialised).
 * This means you can compare *any* geometry against *any* layer.
 */
export function useNearestByGeometry(
  options: UseNearestByGeometryOptions,
): UseNearestByGeometryResult {
  const {
    sourceGeometry,
    targetLayer,
    where,
    limit = 5,
    skip = false,
  } = options;

  const [rawResults, setRawResults] = useState<DistanceResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [tableFilter, setTableFilter] = useState('');

  const requestIdRef = useRef(0);

  // Stable serialised key so the effect doesn't re-fire unless the geometry actually changes.
  const sourceGeomKey = useMemo(
    () => (sourceGeometry ? Geometry.fromJSON(sourceGeometry) : null),
    [sourceGeometry]
  );

  // ── Phase 1: query target layer + compute centroid-based distances ───────
  useEffect(() => {
    if (skip || !sourceGeomKey || !targetLayer) {
      setRawResults((prev) => (prev.length ? [] : prev));
      setIsLoading(false);
      setError(undefined);
      return;
    }

    const currentRequestId = ++requestIdRef.current;
    let cancelled = false;

    const run = async () => {
      setIsLoading(true);
      setError(undefined);

      try {
        const allFeatures = await targetLayer.queryFeatures({
          where: where ?? '1=1',
          outSpatialReference: sourceGeomKey?.spatialReference,
          returnGeometry: true,
          outFields: ['*']
        });

        if (cancelled) return;

        // The worker's calculateDistances expects an Extent JSON (it uses
        // Extent.fromJSON → .center). We need to derive an extent from the
        // source geometry which could be a Polygon, Extent, or other type.
        let sourceExtentJSON: Record<string, unknown>;
        const geomType = (sourceGeometry as Record<string, unknown>)?.type;
        if (geomType === 'extent') {
          sourceExtentJSON = sourceGeometry as Record<string, unknown>;
        } else {
          // Reconstruct the geometry to get its extent
          try {
            const poly = Polygon.fromJSON(sourceGeometry);
            sourceExtentJSON = poly.extent.toJSON();
          } catch {
            // Fallback: try as Extent directly
            try {
              const ext = Extent.fromJSON(sourceGeometry);
              sourceExtentJSON = ext.toJSON();
            } catch {
              console.warn('[useNearestByGeometry] Could not extract extent from source geometry');
              return;
            }
          }
        }

        const featuresJSON = allFeatures.toJSON().features;

        const worker = getDistanceWorker();
        const results: DistanceResult[] = await worker.calculateDistances(
          featuresJSON,
          sourceExtentJSON,
        );

        if (cancelled || currentRequestId !== requestIdRef.current) return;

        setRawResults(results);
      } catch (err) {
        if (!cancelled && currentRequestId === requestIdRef.current) {
          setError(err instanceof Error ? err.message : 'Distance computation failed');
        }
      } finally {
        if (!cancelled && currentRequestId === requestIdRef.current) {
          setIsLoading(false);
        }
      }
    };

    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceGeomKey, targetLayer, where, skip]);

  // ── Post-process ─────────────────────────────────────────────────────────
  const processedRows = useMemo(
    () =>
      postProcessRows(rawResults, {
        limit,
      }),
    [rawResults, limit],
  );
  // Build propId → raw feature JSON lookup for edge-to-edge enrichment
  const rawResultsByObjId = useMemo(() => {
    const map = new Map<number, Record<string, unknown>>();
    for (const r of rawResults) {
      if (!r.property?.attributes) continue;
      const attrs = r.property.attributes as Record<string, unknown>;
      const propId = Number(attrs.OBJECTID);
      map.set(propId, r.property);
    }
    return map;
  }, [rawResults]);

  // ── Phase 2: edge-to-edge enrichment for the small final set ─────────────
  const [enrichedRows, setEnrichedRows] = useState<NearestPropertyRow[]>([]);

  useEffect(() => {
    if (!sourceGeometry || processedRows.length === 0) {
      setEnrichedRows(processedRows);
      return;
    }

    let cancelled = false;

    const enrich = async () => {
      const targets = processedRows
        .map((row) => {
          const featureJSON = rawResultsByObjId.get(Number(row.objectId));
          if (!featureJSON) return null;
          return { propId: row.propId, objectId: row.objectId, featureJSON };
        })
        .filter(Boolean) as Array<{ propId: number; objectId: number; featureJSON: Record<string, unknown> }>;

      if (targets.length === 0) {
        if (!cancelled) setEnrichedRows(processedRows);
        return;
      }

      try {
        const worker = getDistanceWorker();
        const edgeResults = await worker.computeEdgeToEdge(sourceGeometry, targets);

        if (cancelled) return;

        const edgeMap = new Map(edgeResults.map((r) => [r.objectId, r]));
        if (!geodeticLengthOperator.isLoaded() && !geodeticDensifyOperator.isLoaded()) {
          await Promise.all([geodeticLengthOperator.load(), geodeticDensifyOperator.load()]);
        }
        const rows = processedRows.map((row) => {
          const edge = edgeMap.get(Number(row.objectId));
          if (!edge) return row;
          const polyline = new Polyline({
            paths: edge.paths,
            spatialReference: edge.spatialReference,
          });

          const distanceMiles = geodeticLengthOperator.execute(polyline, { unit: 'miles' });
          const geodesicLine = geodeticDensifyOperator.execute(polyline, 200);
          return { ...row, distanceMiles, lineGraphic: geodesicLine?.toJSON() };
        });

        rows.sort((a, b) => (a.distanceMiles ?? a.distanceRaw) - (b.distanceMiles ?? b.distanceRaw));
        if (!cancelled) setEnrichedRows(rows);
      } catch (err) {
        console.error('[useNearestByGeometry] edge-to-edge enrichment failed', err);
        if (!cancelled) setEnrichedRows(processedRows);
      }
    };

    enrich();
    return () => { cancelled = true; };
  }, [processedRows, sourceGeometry, rawResultsByObjId]);

  // ── Client-side table filter ─────────────────────────────────────────────
  const filteredRows = useMemo(
    () => filterRows(enrichedRows, tableFilter),
    [enrichedRows, tableFilter],
  );

  const rowsByType = useMemo(
    () => groupByManagementType(filteredRows),
    [filteredRows],
  );

  const applyTableFilter = useCallback((value: string) => setTableFilter(value), []);
  const clearTableFilter = useCallback(() => setTableFilter(''), []);

  return {
    rows: filteredRows,
    rowsByType,
    total: processedRows.length,
    isLoading,
    error,
    applyTableFilter,
    clearTableFilter,
    tableFilter,
  };
}

